import { useEffect, useId, useRef, useState } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  className?: string;
  /**
   * When true, ESC + overlay-click do nothing and the close button is hidden.
   * Source: BRIEF_ia-redesign-frontend-implementation § 10.1 default —
   * LoginAppSelectionModal blocks access until selection completes.
   */
  disableClose?: boolean;
}

export default function Modal({
  open,
  onClose,
  title,
  children,
  className,
  disableClose = false,
}: ModalProps) {
  const { t } = useTranslation();
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Enter / exit on the same path (ENG-APP-UX-TUNEUP Track F slice 3): a
  // closed dialog unmounts only after its exit animation ends, so `open`
  // flipping false moves the dialog to "closing" instead of returning null.
  // The phase is adjusted from the prop during render (the React pattern for
  // state that follows a prop), never in an effect body.
  const [phase, setPhase] = useState<"closed" | "open" | "closing">(
    open ? "open" : "closed",
  );
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    setPhase(open ? "open" : phase === "closed" ? "closed" : "closing");
  }
  const closing = phase === "closing";

  // Safety net for the closing phase: `animationend` is what unmounts the
  // dialog (the reduced-motion gate zeroes the duration, and a 0 ms animation
  // still ends), but a browser that never fires it would strand a closed
  // dialog on screen -- the timer closes it after the longest motion token.
  useEffect(() => {
    if (!closing) return undefined;
    const timer = window.setTimeout(() => setPhase("closed"), 480);
    return () => window.clearTimeout(timer);
  }, [closing]);

  useEffect(() => {
    if (disableClose) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, onClose, disableClose]);

  // A11y focus management: move focus into the dialog on open, trap Tab within
  // it while open, and restore focus to the trigger on close. Applies even when
  // ``disableClose`` (a blocking modal still owns keyboard focus).
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    dialog?.focus();
    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !dialog) return;
      const focusables = dialog.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleTab);
    return () => {
      document.removeEventListener("keydown", handleTab);
      previouslyFocused?.focus?.();
    };
  }, [open]);

  // Lock body scroll while open so the background page doesn't scroll behind
  // the overlay. The scrollbar's width is re-added as right padding so locking
  // doesn't shift the page layout. The previous inline values are captured and
  // restored on close, which keeps stacked modals correct (an inner modal
  // restores to the outer modal's locked state; the outermost restores the
  // original).
  useEffect(() => {
    if (!open) return;
    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousPaddingRight = body.style.paddingRight;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      const basePadding =
        parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${basePadding + scrollbarWidth}px`;
    }
    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPaddingRight;
    };
  }, [open]);

  if (phase === "closed") return null;

  return (
    <div
      ref={overlayRef}
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm",
        closing
          ? "motion-safe:animate-overlay-exit"
          : "motion-safe:animate-overlay-enter",
      )}
      onClick={(e) => {
        if (disableClose) return;
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          "w-full max-w-lg rounded-xl bg-surface shadow-xl outline-none",
          // Motion-token consumption (BRANDING.md v1.2 § 4.7): the dialog
          // animations carry the short duration + decelerate / accelerate
          // easings from the theme, so the universal :root reduced-motion
          // gate (0 ms) collapses both to an instant swap.
          closing
            ? "motion-safe:animate-dialog-exit"
            : "motion-safe:animate-dialog-enter",
          className,
        )}
        onAnimationEnd={() => {
          if (closing) setPhase("closed");
        }}
      >
        <div className="flex items-center justify-between border-b border-divider px-6 py-4">
          <h2 id={titleId} className="text-lg font-semibold text-ink">
            {title}
          </h2>
          {!disableClose && (
            <button
              onClick={onClose}
              className="press rounded-md p-1 text-muted hover:bg-divider-soft hover:text-ink-soft"
              aria-label={t("ui.modal.closeModalAriaLabel")}
            >
              <X size={18} />
            </button>
          )}
        </div>
        <div className="px-6 py-4">{children}</div>
      </div>
    </div>
  );
}
